<template>
  <v-dialog
    v-model="opened"
    width="600"
    persistent
    @keydown.esc="$emit('update:opened', false)"
  >
    <v-card block flat>
      <v-card-title class="justify-space-between">
        <h2>Make Payment</h2>

        <v-btn icon flat color="error" @click="$emit('update:opened', false)">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="text-xs-left">
        <v-layout class="mb-4" column align-center>
          <h3 v-if="getVoucherDefinitionById(currentVoucher.voucher_id)">
            {{ getVoucherDefinitionById(currentVoucher.voucher_id).name }}
          </h3>
          <h1>{{ formatCurrency(currentVoucher.amount) }}</h1>
        </v-layout>

        <v-layout class="mb-2" justify-center>
          <span
            >To make a payment, please select one of the following
            options:</span
          >
        </v-layout>

        <v-layout
          class="mb-2"
          justify-center
          v-if="currentVoucher.payment_instruction_link"
        >
          <b
            >For instructions on how to pay online, please
            <a target="_blank" :href="currentVoucher.payment_instruction_link"
              >click here</a
            >.</b
          >
        </v-layout>

        <v-layout column class="mt-1">
          <v-layout
            class="payment-option"
            @click="goToPayment()"
            align-start
            v-if="!isEmpty(currentVoucher.payment_link)"
          >
            <v-icon class="mr-3 primary--text">credit_card</v-icon>

            <v-layout column>
              <h4>Pay online</h4>
              <span>Submit a payment online via the link below.</span>
            </v-layout>
          </v-layout>

          <v-layout
            class="payment-option"
            @click="downloadVoucher()"
            align-start
            v-if="!currentVoucher.pay_by_check_disabled"
          >
            <v-icon class="mr-3 material-icons-outlined primary--text"
              >mail</v-icon
            >

            <v-layout column>
              <h4>Pay by check</h4>
              <span
                >Click here to download the voucher and send your check to the
                address indicated on the voucher.</span
              >
            </v-layout>
          </v-layout>
        </v-layout>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import API from '@/api';
import { PDFDocument } from 'pdf-lib';
import download from 'downloadjs';
import isEmpty from 'lodash/isEmpty';
import UtilsMixins from '@/mixins/UtilsMixins';

export default {
  mixins: [UtilsMixins],

  props: {
    opened: {
      type: Boolean,
      default: false,
    },

    companyId: {
      type: String,
      default: undefined,
    },

    currentVoucher: {
      type: Object,
      default: () => {},
    },

    taxReturn: {
      type: Object,
      default: () => {},
    },

    voucherDefinitions: {
      type: Array,
      default: () => [],
    },
  },

  methods: {
    isEmpty,

    goToPayment() {
      if (this.currentVoucher.payment_link) {
        window.open(this.currentVoucher.payment_link, '_blank');
      }
    },

    async downloadVoucher() {
      const voucherDefinition = this.getVoucherDefinitionById(
        this.currentVoucher.voucher_id,
      );

      if (this.currentVoucher.document_location) {
        const response = await API.integration.downloadDocument({
          companyId: this.companyId,
          link: this.currentVoucher.document_location,
        });

        await this.downloadDocumentByLink(
          response.data.document.data.link,
          voucherDefinition.client_facing_name + '.pdf',
        );
      } else {
        const response = await API.integration.downloadDocument({
          companyId: this.companyId,
          link: this.taxReturn.watermark_enabled
            ? this.taxReturn.watermarked_file
            : this.taxReturn.original_file,
        });

        const pdfSrc = response.data.document.data.link;
        const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());

        const pdfDoc = await PDFDocument.load(pdfBytes);

        const voucherPdf = await PDFDocument.create();
        const [voucherPage] = await voucherPdf.copyPages(pdfDoc, [
          this.currentVoucher.page - 1,
        ]);
        voucherPdf.addPage(voucherPage);

        const voucherData = await voucherPdf.saveAsBase64({ dataUri: true });

        return download(
          voucherData,
          voucherDefinition.client_facing_name + '.pdf',
          'application/pdf',
        );
      }
    },

    getVoucherDefinitionById(id) {
      return this.voucherDefinitions.find(voucher => voucher.id === id);
    },

    formatCurrency(amount) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      });

      return formatter.format(amount);
    },
  },
};
</script>

<style scoped lang="postcss">
.payment-option {
  cursor: pointer;
  padding: 15px;
  border: 1px solid #e5e5e5;
  border-radius: 5px;
  margin-bottom: 10px;

  &:hover {
    background-color: #f5f5f5;
  }
}
</style>
